import { Button, Space, Typography, InputNumber } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { TradeConfirmationRow, BondDistributionFile } from "./types";
import { usePart2 } from "./usePart2";
import { SimpleUploadSection, AccumulativeUploadSection } from "./components/FileUploadSection";
import { CustomerBondFilesSection } from "./components/CustomerBondFilesSection";
import { ConfigSection } from "./components/ConfigSection";

const { Text } = Typography;

const dividerStyle: React.CSSProperties = {
  height: 1,
  background: "#f0f0f0",
  margin: "8px 0",
};

interface Part2Props {
  combinedTC: TradeConfirmationRow[];
  bondDistributionData: BondDistributionFile[];
  onBack: () => void;
}

export const Part2 = ({ combinedTC, bondDistributionData, onBack }: Part2Props) => {
  const {
    detailedWithdrawData,
    custodyInstructionData,
    accumulatedSoldStockSheets,
    accumulatedChargeSheets,
    customerBondFiles,
    interestEndDate,
    usdRate,
    userBankInfoMap,
    missingSheet8UsdEntries,
    manualSheet8UsdRates,
    detailedWithdrawRequestFiles,
    purchaseHistoryFiles,
    bankStatementFiles,
    custodyInstructionFiles,
    bondMetadataFiles,
    part2Loading,
    lookupLoading,
    customerDataLoading,
    handleDetailedWithdrawUpload,
    handlePurchaseHistoryUpload,
    handleBankStatementUpload,
    handleCustodyInstructionUpload,
    handleBondMetadataUpload,
    handleSoldStockFoundation,
    handleSoldStockAddSheet,
    handleChargeFoundation,
    handleChargeAddSheet,
    handleCustomerPurchaseFile,
    handleCustomerTxHistoryFile,
    fetchCustomerDataFromAPI,
    handlePart2Process,
    excludedUsers,
    toggleExcludeUser,
    setInterestEndDate,
    setUsdRate,
    setManualSheet8UsdRates,
    setAccumulatedSoldStockSheets,
    setAccumulatedChargeSheets,
    setCustomerBondFiles,
  } = usePart2(combinedTC, bondDistributionData);

  return (
    <section>
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 24,
          }}
        >
        {/* 1. Дэлгэрэнгүй буцаалт */}
        <SimpleUploadSection
          label="1. Дэлгэрэнгүй буцаалт"
          files={detailedWithdrawRequestFiles}
          onUpload={handleDetailedWithdrawUpload}
        />

        {/* 2. Худалдан авалтын задаргаа */}
        <SimpleUploadSection
          label="2. Худалдан авалтын задаргаа"
          files={purchaseHistoryFiles}
          onUpload={handlePurchaseHistoryUpload}
        />

        {/* 3. SG хуулга */}
        <SimpleUploadSection
          label="3. SG хуулга"
          files={bankStatementFiles}
          onUpload={handleBankStatementUpload}
        />

        {/* 4. Custody Instructions ханш */}
        <SimpleUploadSection
          label="4. Custody Instructions ханш"
          files={custodyInstructionFiles}
          onUpload={handleCustodyInstructionUpload}
          accept=".csv,.xlsx,.xls"
        />

        {/* 5. Хувьцаа дахин цэнэглэлт (accumulative) */}
        <AccumulativeUploadSection
          label="5. Хувьцаа дахин цэнэглэлт"
          sheets={accumulatedSoldStockSheets}
          onFoundation={handleSoldStockFoundation}
          onAddSheet={handleSoldStockAddSheet}
          onDownload={() => {}}
          onClear={() => setAccumulatedSoldStockSheets([])}
        />

        {/* 6. Бондын мэдээлэл */}
        <SimpleUploadSection
          label="6. Бондын мэдээлэл"
          files={bondMetadataFiles}
          onUpload={(fileList) => handleBondMetadataUpload(fileList)}
        />

        {/* 7. SG Цэнэглэлт (accumulative) */}
        <AccumulativeUploadSection
          label="7. SG Цэнэглэлт"
          sheets={accumulatedChargeSheets}
          onFoundation={handleChargeFoundation}
          onAddSheet={handleChargeAddSheet}
          onDownload={() => {}}
          onClear={() => setAccumulatedChargeSheets([])}
        />
        </div>

        {missingSheet8UsdEntries.length > 0 && (
          <div>
            <Text strong type="danger">Sheet 8 missing USD ханш:</Text>
            <div style={{ marginTop: 6 }}>
              <Text type="secondary">
                Custody file дээр USD хоосон байсан огноонууд. Зөв USD-MNT ханшийг ажилтнаас асууж огноо тус бүр дээр оруулна уу.
              </Text>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              {missingSheet8UsdEntries.map((entry) => (
                <div
                  key={entry.key}
                  style={{
                    border: "1px solid #ffccc7",
                    background: "#fff2f0",
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <div>
                    <Text strong>{entry.date || "-"}</Text>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <Text style={{ display: "block", marginBottom: 4 }}>Гараар оруулах USD-MNT ханш:</Text>
                    <InputNumber
                      min={1}
                      max={10000}
                      step={1}
                      value={manualSheet8UsdRates[entry.key]}
                      onChange={(value) => {
                        const numericValue = Number(value) || 0;
                        setManualSheet8UsdRates((prev: Record<string, number>) => ({
                          ...prev,
                          [entry.key]: numericValue,
                        }));
                      }}
                      style={{ width: 180 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 9. Per-customer bond files */}
        <CustomerBondFilesSection
          detailedWithdrawData={detailedWithdrawData}
          customerBondFiles={customerBondFiles}
          customerDataLoading={customerDataLoading}
          excludedUsers={excludedUsers}
          onFetchFromAPI={fetchCustomerDataFromAPI}
          onCustomerPurchaseFile={handleCustomerPurchaseFile}
          onCustomerTxHistoryFile={handleCustomerTxHistoryFile}
          onClearAll={() => setCustomerBondFiles({})}
          onToggleExclude={toggleExcludeUser}
        />

        <div style={dividerStyle} />

        <ConfigSection
          interestEndDate={interestEndDate}
          usdRate={usdRate}
          onInterestEndDateChange={setInterestEndDate}
          onUsdRateChange={setUsdRate}
        />

        <div style={dividerStyle} />

        {/* User Bank Info Lookup Status */}
        {Object.keys(userBankInfoMap).length > 0 && (
          <div>
            <Text type="success">
              <SearchOutlined /> {Object.keys(userBankInfoMap).length} хэрэглэгчийн банкны мэдээлэл олдсон
            </Text>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
          }}
        >
          <Button onClick={onBack}>← Буцах</Button>
          <Button
            type="primary"
            size="large"
            onClick={handlePart2Process}
            loading={part2Loading || lookupLoading}
            disabled={detailedWithdrawData.length === 0 || combinedTC.length === 0 || custodyInstructionData.length === 0}
            style={{ minWidth: 160 }}
          >
            {lookupLoading ? "Хайж байна..." : "Боловсруулах"}
          </Button>
        </div>
      </Space>
    </section>
  );
};
